import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { GetStaticPaths, GetStaticProps } from 'next/types';
import { Image } from '@vgn-medien-holding/vgn-fe-components';
import {
  AditionTagPlacement,
  AditionInitialization,
  AditionTagWithFallback,
} from '@vgn-medien-holding/vgn-fe-components';
import { getCookie } from 'cookies-next';
import { twMerge } from 'tailwind-merge';
import { Button } from '@components/atoms/Button/Button';
import { EntityInfo } from '@components/atoms/EntityInfo/EntityInfo';
import { EntityScore } from '@components/atoms/EntityScore/EntityScore';
import { EntitySectionTitle } from '@components/atoms/EntitySectionTitle/EntitySectionTitle';
import { Genre } from '@components/atoms/Genre/Genre';
import { LoadingIndicator } from '@components/atoms/LoadingIndicator/LoadingIndicator';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { StructuredData } from '@components/atoms/MetaTags/StructuredData';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { Title } from '@components/atoms/Title';
import { Episode } from '@components/molecules/Episode/Episode';
import { FavoriteButton } from '@components/molecules/FavoriteButton/FavoriteButton';
import { Person } from '@components/molecules/Person/Person';
import { DefaultNoSpacing } from '@components/templates/layouts/DefaultNoSpacing';
import { isUuid } from '@utils/common';
import { providers } from '@utils/providers';
import { GetBySlugDocument } from '@src/lib/graphql/generated';
import { useFavoritesStore } from '@src/stores/favorites';
import { decodeOptions } from '@src/utils/urlHelpers';
import {
  GetSeasonEpisodesDocument,
  GetShowByIdDocument,
  GetShowBySlugDocument,
  GetShowPersonsDocument,
} from '@lib/graphql/generated';
import { createUrqlClient, usePaginatedQuery } from '@lib/graphql/urql';
import { useTagManager } from '@lib/hooks/useTagManager';
import { handleRedirect } from '@lib/redirects/redirect';

export function getSeasonCount(seasons) {
  if (!seasons || seasons?.data?.length < 1) return 0;
  return seasons.data
    .map((e) => e.nr)
    .reduce((prev, next) => {
      return prev > next ? prev : next;
    });
}

// Unterseite Serien
const ad_settings_UnterseiteSerien = {
  billboard: {
    id: 5256691,
    name: 'TVM_Unterseite_Serien_Billboard',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  right1: {
    id: 5256694,
    name: 'TVM_Unterseite_Serien_HPA',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  mobile_top: {
    id: 5256614,
    name: 'TVM_Mobile_Unterseite_Serien_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle1: {
    id: 5256612,
    name: 'TVM_Mobile_Unterseite_Serien_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

function getProviderFromSlug(slug) {
  return providers.find((e) => e.slug === slug);
}

function Show({ data }) {
  const { show, oewa } = data;
  const heroImage = show?.backdrop_image || show?.cover_image;
  const ogImage = show?.backdrop_image_og || show?.cover_image_og;

  const [coverLoaded, setCoverLoaded] = useState(false);
  const [activeSeason, setActiveSeason] = useState(data.activeSeason);

  const { getFavoriteState } = useFavoritesStore((state) => ({
    getFavoriteState: state.getFavoriteState,
  }));

  const [
    {
      fetching: personsLoading,
      stale: personsStale,
      data: personsData,
      hasMorePages: hasPersonsMorePages,
      fetchNextPage: fetchNextPersonsPage,
    },
  ] = usePaginatedQuery({
    query: GetShowPersonsDocument,
    variables: { id: show?.id, first: 10, page: 1 },
  });
  const persons = personsData?.flatMap((data) => data?.tvshow?.persons?.data);

  const [
    {
      fetching: episodesLoading,
      stale: episodesStale,
      data: episodesData,
      currentPage: episodesPage,
      hasMorePages: hasEpisodesMorePages,
      fetchNextPage: fetchNextEpisodesPage,
      fetchSpecificPage: fetchSpecificEpisodesPage,
    },
  ] = usePaginatedQuery({
    query: GetSeasonEpisodesDocument,
    variables: { id: activeSeason, first: 15, page: 1 },
  });
  const episodes = episodesData?.flatMap((data) => data?.season?.episodes?.data);

  useTagManager({
    event: 'metadata',
    loginStatus: 'nicht eingeloggt',
    seitentyp: 'Serie',
    titel: show?.title,
    assetID: oewa?.id,
    aktualisierungsdatum: data?.updated_at,
    erstellungsdatum: data?.created_at,
    oewaSeitenkontingent: oewa?.page_key || 'Service',
    oewaSubkontingent: oewa?.category_key || 'Programmeundlokalfuehrer',
    oewaProfilingkontingent: oewa?.profile_key || 'TVProgramme',
    cookieAlert: getCookie('OptanonConsent'),
  });

  useEffect(() => {
    setActiveSeason(show?.seasons?.data?.filter((e) => e.nr > 0)?.[0]?.id);
  }, [show]);

  return (
    <>
      <Oewa
        pageKey={oewa?.page_key || 'RedCont'}
        categoryKey={oewa?.category_key || 'KulturUndFreizeit'}
        profileKey={oewa?.profile_key || 'FilmUndKino'}
      />
      <AditionInitialization data={{ ad_settings: ad_settings_UnterseiteSerien }} />
      <Head>
        {Meta({ ...data.show.metadata, open_graph_image: ogImage?.url }, data.show.metadata.meta_title + ' | TV-MEDIA')}
      </Head>
      <StructuredData
        entry={show}
        image={ogImage?.url}
        actors={persons.filter((p) => p.meta?.positions.includes('Schauspieler'))}
      />

      <div className="relative h-[120vh] w-full text-white portrait-giant:h-[640px]">
        {heroImage?.url && (
          <Image
            src={heroImage.url}
            alt={show.title}
            fill
            copyright={heroImage.copyright_text}
            classProps={{ container: 'inset-0 absolute', root: 'object-cover' }}
            onLoaded={() => {
              setCoverLoaded(true);
            }}
            priority
            sizes="(max-height: 1600px) 1920px, 100vw"
          />
        )}
        <div
          className={twMerge(
            'absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black transition-all duration-500',
            !coverLoaded && 'from-black/0 via-black/0',
          )}
        ></div>
        <div className="relative grid h-screen w-full grid-rows-[1fr,auto] portrait-giant:h-[640px]">
          <div className="relative grid content-center items-center gap-y-12 px-8">
            <Title
              level={1}
              classProps={{
                heading: 'hyphenate opacity-75 text-center text-4xl md:text-6xl lg:text-7xl lg:leading-smaller',
              }}
            >
              {show?.title}
            </Title>
            <div className="flex flex-wrap justify-center gap-4 text-center">
              {episodes?.[0]?.links.length > 0 && (
                <Button
                  title={`Jetzt ${
                    episodes?.[0]?.links.length === 1
                      ? 'auf ' + getProviderFromSlug(episodes?.[0]?.links[0].provider)?.name
                      : ''
                  } streamen`}
                  link={`/serien/${show.slug || show.id}#seasons`}
                  buttonStyle="tertiary"
                />
              )}
            </div>
            {show?.trailer?.length > 0 && (
              <div className="text-center">
                <Button
                  title="Trailer ansehen"
                  link={show.trailer?.[0]?.url}
                  buttonStyle="viewLink"
                  classProps={{ root: 'mt-6' }}
                />
              </div>
            )}
          </div>
          <div>
            <aside className="grid w-full items-center divide-y divide-gray-650/25 border-y border-gray-650/25 bg-gray-820/50 px-8 py-4 backdrop-blur-md md:grid-cols-[1fr,auto,1fr] md:justify-start md:gap-6 md:divide-none md:divide-transparent md:px-16 md:py-6">
              <div className="flex min-w-0 justify-center gap-6 py-4 md:justify-start md:py-0">
                {show?.tvm_score !== 0 ? (
                  <EntityInfo label="Bewertung">
                    <EntityScore max={4} score={show?.tvm_score - 1} />
                  </EntityInfo>
                ) : show?.tmdb_score ? (
                  <EntityInfo
                    label="TMDB-Rating"
                    value={show.tmdb_score?.toLocaleString('de-DE', { maximumFractionDigits: 1 })}
                    unit="%"
                  />
                ) : (
                  <EntityInfo label="TMDB-Rating" value="-" />
                )}
              </div>
              <div className="flex min-w-0 flex-wrap justify-center gap-10 gap-y-6 py-4 sm:min-w-[300px] md:py-0">
                <EntityInfo label="Jahr" value={show?.year} />
                <EntityInfo label="Alter" value={show?.age_rating} />
                <EntityInfo label="Staffeln" value={getSeasonCount(show?.seasons)} />
              </div>
              <div className="contents items-center justify-end gap-4 md:flex">
                <div className="flex min-h-20 flex-wrap items-center justify-center gap-4 border-t border-gray-650/25 py-5 md:min-h-0 md:gap-2 md:border-none md:py-0">
                  {show?.genres?.map((genre) => <Genre key={genre.id} genre={genre} />)}
                </div>
                <div className="flex min-h-16 items-center justify-center gap-4 border-t border-gray-650/25 pt-5 md:min-h-0 md:border-none md:pt-0">
                  <FavoriteButton
                    id={show?.id}
                    type="Show"
                    classProps={{ root: 'text-white' }}
                    loginFlowTitle="Speichere jetzt Deine Lieblingsserien und -filme"
                  >
                    <span className="label-sm text-gray-400 md:hidden">
                      {getFavoriteState(show?.id) ? 'Aus der Watchlist entfernen' : 'Auf die Watchlist geben'}
                    </span>
                  </FavoriteButton>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
      <div className="relative z-10 mt-[-20vh] text-white prose-a:text-white portrait-giant:mt-0">
        <main>
          <div className="relative mx-auto grid max-w-screen-xl grid-cols-[auto,1fr] gap-4 pt-16">
            <article className="w-full max-w-screen-md divide-y divide-gray-650 px-8 md:px-16">
              {show?.summary && (
                <section>
                  <EntitySectionTitle>Beschreibung</EntitySectionTitle>
                  <div className="prose prose-streaming" dangerouslySetInnerHTML={{ __html: show?.summary }}></div>
                </section>
              )}
              {((!!show?.review && show?.review !== '<p></p>') || show?.tvm_score !== 0) && (
                <section className="mt-12 pt-12">
                  <div>
                    <EntitySectionTitle classProps={{ root: 'mt-0' }}>TV-Media Bewertung</EntitySectionTitle>
                    {show?.tvm_score !== 0 && <EntityScore max={4} score={show.tvm_score - 1} />}
                    {show.review && (
                      <div
                        className="prose prose-streaming pt-2"
                        dangerouslySetInnerHTML={{ __html: show.review }}
                      ></div>
                    )}
                  </div>
                </section>
              )}
            </article>
            <div className="grid justify-end">
              <AditionTagPlacement
                tag={ad_settings_UnterseiteSerien.right1}
                classProps={{ root: 'sticky top-28 mb-4 hidden md:block' }}
              />
            </div>
            <AditionTagPlacement
              tag={ad_settings_UnterseiteSerien.mobile_top}
              classProps={{ root: 'mt-8 w-full grid place-items-center md:hidden' }}
            />
          </div>
          <article className="mt-16 px-8 lg:px-16">
            {persons?.length > 0 && (
              <>
                <Title level={2} classProps={{ heading: 'text-base lg:text-xl' }}>
                  Darsteller und Crew
                </Title>
                <LoadingIndicator visible={persons?.length < 1 && personsLoading} />
                <div className="mt-6 grid grid-cols-fill-actors justify-items-stretch gap-4">
                  {persons?.map((person) => <Person key={person.id} person={person} classProps={{ root: 'w-full' }} />)}
                </div>
                {hasPersonsMorePages && (
                  <div className="text-center">
                    <Button
                      title="Mehr anzeigen"
                      hasAction
                      isLoading={personsLoading || personsStale}
                      onAction={() => setTimeout(fetchNextPersonsPage)}
                      buttonStyle="secondary"
                      classProps={{ root: 'mt-6' }}
                    />
                  </div>
                )}
              </>
            )}
            <div className="grid place-items-center">
              <AditionTagWithFallback
                tag={ad_settings_UnterseiteSerien.billboard}
                fallback={ad_settings_UnterseiteSerien.mobile_middle1}
                breakpoint={'lg'}
                classProps={{ root: 'mt-18' }}
              />
            </div>
          </article>
          {show?.seasons?.data?.length > 0 && (
            <article className="px-4 pt-16 sm:px-8 lg:px-16" id="seasons">
              <div className="grid w-full lg:flex lg:items-center lg:justify-between">
                <Title level={2} classProps={{ heading: 'text-3xl leading-11 lg:text-4xl lg:leading-12' }}>
                  Episoden
                </Title>
                <select
                  className="input-tvm-classic mt-8"
                  defaultValue={show.seasons.data[0].id}
                  onChange={(item) => {
                    fetchSpecificEpisodesPage(1);
                    setActiveSeason(item?.target.value);
                  }}
                >
                  {show.seasons?.data
                    ?.filter((e) => e.nr > 0)
                    .map(({ id, nr }) => (
                      <option key={id} value={id}>
                        Staffel {nr}
                      </option>
                    ))}
                </select>
              </div>
              <div className="mt-6 grid gap-4">
                {(episodesLoading || episodesStale) && !episodesPage ? (
                  <LoadingIndicator visible />
                ) : (
                  <>
                    {episodes?.map((episode) => <Episode key={episode.id} episode={episode} />)}
                    {hasEpisodesMorePages && (
                      <div className="text-center">
                        <Button
                          title="Mehr anzeigen"
                          hasAction
                          isLoading={episodesLoading || episodesStale}
                          onAction={() => setTimeout(fetchNextEpisodesPage)}
                          buttonStyle="secondary"
                          classProps={{ root: 'mt-6' }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </article>
          )}
        </main>
      </div>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { ssrQuery, ssrCache } = createUrqlClient();

  const options = decodeOptions(params?.slug);

  const { data: showData } = isUuid(options?.slug)
    ? await ssrQuery({
        query: GetShowByIdDocument,
        variables: { id: options.slug, personCount: 10 },
      })
    : await ssrQuery({
        query: GetShowBySlugDocument,
        variables: { slug: options.slug, personCount: 10 },
      });
  const show = showData?.tvshow;

  if (!show?.published) {
    return {
      redirect: {
        permanent: false,
        destination: '/404',
      },
    };
  }

  if (!show) {
    // Check if a redirect is in place
    const { data: pageData } = await ssrQuery({
      query: GetBySlugDocument,
      variables: {
        portal: 'tvmedia',
        slug: `/serien/${options.slug}`,
      },
    });

    // Handle redirect result
    if (pageData?.findBySlug?.__typename === 'Redirect' && pageData?.findBySlug?.redirect_type) {
      return handleRedirect(
        pageData?.findBySlug?.redirect_type,
        pageData?.findBySlug?.redirect_target != null ? pageData?.findBySlug?.redirect_target : '',
      );
    }
  }

  if (!show) return { notFound: true };

  const activeSeason = show?.seasons?.data?.filter((e) => e.nr > 0)?.[0]?.id;

  await ssrQuery({
    query: GetSeasonEpisodesDocument,
    variables: { id: activeSeason, page: 1, first: 15 },
  });

  await ssrQuery({
    query: GetShowPersonsDocument,
    variables: { id: show.id, page: 1, first: 10 },
  });

  return {
    props: {
      data: {
        show,
        activeSeason,
      },
      urqlState: ssrCache.extractData(),
    },
  };
};

Show.PageLayout = DefaultNoSpacing;

export default Show;
